import React from 'react'
import { Box } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { Section } from '../../common/Section'
import { BarChart } from '../../common/charts/BarChart'
import {
  getContainerId,
  VersionCostCalculator,
} from '../../../calculators/costs/VersionCostCalculator'
import {
  ISOCostCategory,
  isoCostCategoryToCostTypesMap,
} from '../cost-tables/iso-cost-category'
import { formatPrice } from '../../../util/formatters'

type Props = {
  versions: VersionCostCalculator[]
  selectedContainerName: string | null
  containerIdsToNames: Record<string, string>
  printPadding?: boolean
}

export function CostBreakdownBarChart({
  versions,
  selectedContainerName,
  containerIdsToNames,
  printPadding,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const keys = Object.values(ISOCostCategory)
  const xFormatter = (category: string) =>
    t(`costs.ISOCostCategory.${category}`)

  const getCumulativeOtherSumForVersion = (
    version: VersionCostCalculator,
    category: Exclude<ISOCostCategory, ISOCostCategory.Energy>,
  ) => {
    if (selectedContainerName) {
      const containerId = getContainerId(
        version,
        containerIdsToNames,
        selectedContainerName,
      )
      return containerId
        ? version.getCumulativeOtherSum(
            isoCostCategoryToCostTypesMap[category],
            containerId,
          )
        : 0
    }
    return version.getCumulativeOtherSum(
      isoCostCategoryToCostTypesMap[category],
    )
  }

  const getCumulativeEnergySumForVersion = (version: VersionCostCalculator) => {
    if (selectedContainerName) {
      const containerId = getContainerId(
        version,
        containerIdsToNames,
        selectedContainerName,
      )
      return containerId ? version.getCumulativeEnergySum(containerId) : 0
    }
    return version.getCumulativeEnergySum()
  }

  const mapVersionToData = (version: VersionCostCalculator) => ({
    id: version.getName(),
    [ISOCostCategory.Initial]: getCumulativeOtherSumForVersion(
      version,
      ISOCostCategory.Initial,
    ),
    [ISOCostCategory.Recurring]: getCumulativeOtherSumForVersion(
      version,
      ISOCostCategory.Recurring,
    ),
    [ISOCostCategory.Maintenance]: getCumulativeOtherSumForVersion(
      version,
      ISOCostCategory.Maintenance,
    ),
    [ISOCostCategory.Energy]: getCumulativeEnergySumForVersion(version),
  })

  const data = versions.map(mapVersionToData)

  const containerHeight = printPadding ? 500 : 550

  return (
    <Section
      title={t('costs.results.breakdownChartTitle')}
      printPadding={printPadding}
    >
      <Box width="100%" height={containerHeight} borderRadius={6}>
        {data.length > 0 && (
          <BarChart
            data={data}
            keys={keys}
            xFormatter={xFormatter}
            yFormatter={formatPrice}
          />
        )}
      </Box>
    </Section>
  )
}
